<script lang="ts">
	import type { B2bCustomerCreateInput } from "./../../../core/schema/B2bCustomer.js";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import { createPromiseStore } from "../../../core/utils/createPromiseStore.js";
	import { getClients } from "../appsync/getClients.js";
	import createB2bCustomer from "../appsync/mutations/createB2bCustomer.graphql";
	import cake from "../../../core/assets/icons/cake.svg?raw";
	import LabelledInput from "./Form/LabelledInput.svelte";
	import Button from "./Button.svelte";
	import Modal from "./Modal.svelte";
	import Form from "./Form/Form.svelte";
	import Message from "./Message.svelte";
	import { objectToOptions } from "../../../core/utils/objectToOptions";
	import Smartform from "./Smartform.svelte";
	import { type B2bApplicantRole, b2bCustomerApplicantRoleNameMap } from "../../../core/schema/B2bApplicantRole.js";
	import {
		type B2bPresumedOrderAmount,
		b2bCustomerPresumedOrderAmountNameMap,
	} from "../../../core/schema/B2bPresumedOrderAmount.js";
	import {
		b2bCustomerPresumedDeliveryIntervalNameMap,
		type B2bPresumedDeliveryInterval,
	} from "../../../core/schema/B2bPresumedDeliveryInterval.js";
	import { expectErrorMessage } from "./Form/expectErrorMessage.js";
	import LabelledRadioInput from "./Form/LabelledRadioInput.svelte";
	import {
		type SmartFormAddressResult,
		SmartFormAddressValidationResultType,
		type SmartFormCompanyResult,
	} from "../../../core/schema/Smartform.js";
	import type { SmartFormAddress } from "../../../core/schema/SmartFormAddress.js";
	import GdprCheckbox from "./GdprCheckbox.svelte";

	const globalSettings = getGlobalSettings();
	const { TENANT_ID, TENANT_LOCALE } = globalSettings;

	const send = createPromiseStore(async (input: B2bCustomerCreateInput) => {
		const { graphQL } = await getClients(globalSettings);
		await graphQL.mutate<{ input: B2bCustomerCreateInput }>({
			mutation: createB2bCustomer,
			variables: { input },
		});
	});

	let smartFormShippingAddress: SmartFormAddress | undefined;
	let showSuccessModal = false;
	let submitted = false;
	async function submit(event: SubmitEvent): Promise<void> {
		submitted = true;
		if (!smartFormShippingAddress) {
			return;
		}

		const form = event.target as HTMLFormElement;
		const formData = new FormData(form);
		const shippingAddressStreet = String(formData.get("shippingAddressStreet"));
		const shippingAddressStreetNumber = String(formData.get("shippingAddressStreetNumber"));
		const shippingAddressCity = String(formData.get("shippingAddressCity"));
		const shippingAddressZip = String(formData.get("shippingAddressZip"));
		const billingStreet = String(formData.get("billingAddressStreet")) || shippingAddressStreet;
		const billingStreetNumber = String(formData.get("billingAddressStreetNumber")) || shippingAddressStreetNumber;
		const billingCity = String(formData.get("billingAddressCity")) || shippingAddressCity;
		const billingZip = String(formData.get("billingAddressZip")) || shippingAddressZip;
		const input: B2bCustomerCreateInput = {
			firstName: String(formData.get("firstName")),
			lastName: String(formData.get("lastName")),
			email: String(formData.get("email")),
			phoneNumber: String(formData.get("phoneNumber")),
			applicantRole: formData.get("applicantRole") as B2bApplicantRole,
			company: {
				cin: String(formData.get("cmpCin")),
				vat: String(formData.get("cmpVat")),
				name: String(formData.get("cmpNameSmartform")),
			},
			billingAddress: {
				street: billingStreet,
				streetNumber: billingStreetNumber,
				city: billingCity,
				zip: billingZip,
			},
			shippingAddress: {
				street: shippingAddressStreet,
				streetNumber: shippingAddressStreetNumber,
				city: shippingAddressCity,
				zip: shippingAddressZip,
				smartFormAddress: smartFormShippingAddress,
			},
			contactAddress: {
				street: billingStreet,
				streetNumber: billingStreetNumber,
				city: billingCity,
				zip: billingZip,
			},
			presumedOrderAmount: formData.get("presumedOrderAmount") as B2bPresumedOrderAmount,
			presumedOrderAmountSpecified: String(formData.get("presumedOrderAmountSpecified")),
			presumedDeliveryInterval: formData.get("presumedDeliveryInterval") as B2bPresumedDeliveryInterval,
			presumedDeliveryIntervalSpecified: String(formData.get("presumedDeliveryIntervalSpecified")),
			companyName: String(formData.get("companyName")),
			tenantId: TENANT_ID,
			gdprApprovedAt: String(formData.get("gdprApprovedAt")),
		};
		await send.call(input);
		form.reset();
		showSuccessModal = true;
	}
	let selectedApplicantRole: B2bApplicantRole | undefined;
	let selectedPresumedOrderAmount: B2bPresumedOrderAmount | undefined;
	let selectedPresumedDeliveryInterval: B2bPresumedDeliveryInterval | undefined;

	const applicantRoleOptions = [...objectToOptions(b2bCustomerApplicantRoleNameMap)];

	const presumedOrderAmountOptions = [...objectToOptions(b2bCustomerPresumedOrderAmountNameMap)];

	const presumedDeliveryIntervalOptions = [...objectToOptions(b2bCustomerPresumedDeliveryIntervalNameMap)];

	$: sendErrorMessage =
		$send.status === "error" &&
		expectErrorMessage(
			$send.error,
			TENANT_LOCALE,
			{
				UsernameExistsException: "Na tento e-mail již byla odeslána poptávka spolupráce.",
			},
			"Něco se pokazilo, zkuste to prosím znovu a později.",
		);

	function onAddressValidation({ detail }: CustomEvent<SmartFormAddressResult>): void {
		const [address] = detail.result.addressArray;
		if (detail.result.type !== SmartFormAddressValidationResultType.HIT || !address) {
			smartFormShippingAddress = undefined;
			return;
		}

		smartFormShippingAddress = address;
	}

	let companyInput = null as HTMLInputElement | null;
	let companyName = null as string | null;
	// Keep space to stop label from jumping
	let companyCin = " " as string;
	let companyDic = " " as string;

	function onCompanyValidation({ detail }: CustomEvent<SmartFormCompanyResult>): void {
		const [company] = detail.result.companyArray;
		companyInput?.setCustomValidity(company ? "" : "Zvolte prosím firmu.");
		if (!company) {
			companyCin = " ";
			companyDic = " ";
		} else {
			companyName = company.NAME;
			companyCin = company.REGISTRATION_NUMBER;
			companyDic = company.VAT_NUMBER;
		}
	}

	/* eslint-disable @typescript-eslint/explicit-function-return-type */
</script>

<Modal class="!max-w-[45rem]" bind:show={showSuccessModal}>
	<p class="h2" slot="title">Děkujeme za žádost o partnerství</p>
	<div class="flex flex-col items-center gap-2 text-center">
		<p>Vaši žádost jsme přijali k vyhodnocení. <br /> O dalším postupu Vás budeme informovat e-mailem.</p>
	</div>
</Modal>
<Smartform on:addressValidation={onAddressValidation} on:companyValidation={onCompanyValidation}>
	<Form onsubmit={submit} enableRecaptcha>
		<div class="relative grid gap-8 pb-24 md:grid-cols-2 md:gap-4 md:pb-0 lg:gap-6">
			<fieldset class="flex flex-col gap-2">
				<legend class="mb-2 text-base font-medium md:mb-3 lg:text-md">Jednám jako*</legend>
				{#each applicantRoleOptions as { value, label }}
					<LabelledRadioInput
						id="applicantRole-{value}"
						name="applicantRole"
						bind:group={selectedApplicantRole}
						{value}
						required
					>
						{label}
					</LabelledRadioInput>
				{/each}
			</fieldset>
			<fieldset class="flex flex-col gap-2">
				<legend class="mb-2 text-base font-medium md:mb-3 lg:text-md">Firemní iniciály</legend>
				<LabelledInput
					id="cmpNameSmartform"
					required
					inputProps={{ class: "smartform-instance-company smartform-company-name" }}
					bind:input={companyInput}
					bind:value={companyName}
				>
					Název firmy
				</LabelledInput>
				<LabelledInput
					id="cmpCin"
					required
					inputProps={{
						pattern: "\\d+",
						readonly: true,
					}}
					bind:value={companyCin}
				>
					IČO
				</LabelledInput>
				<LabelledInput id="cmpVat" inputProps={{ readonly: true, class: "bg-gray-100" }} bind:value={companyDic}>
					DIČ
				</LabelledInput>
			</fieldset>

			<fieldset class="flex flex-col gap-2">
				<legend class="mb-2 text-base font-medium md:mb-3 lg:text-md">Kontaktní údaje</legend>
				<LabelledInput id="firstName" required autocomplete="given-name">Jméno</LabelledInput>
				<LabelledInput id="lastName" required autocomplete="family-name">Příjmení</LabelledInput>
				<LabelledInput id="email" required type="email" autocomplete="email">E-mail</LabelledInput>
				<LabelledInput id="phoneNumber" required type="tel" autocomplete="tel">Telefon</LabelledInput>
			</fieldset>

			<div class="flex flex-col gap-2">
				<fieldset class="flex flex-col gap-2">
					<legend class="mb-2 text-base font-medium md:mb-3 lg:text-md">Kam budeme dorty doručovat</legend>
					<LabelledInput id="companyName" required>Název podniku</LabelledInput>
					<div class="grid gap-x-2 gap-y-2 sm:grid-cols-[1fr,8rem] lg:grid-cols-[1fr,11rem] lg:gap-x-6">
						<LabelledInput
							id="shippingAddressStreet"
							required
							inputProps={{ class: "smartform-instance-address smartform-address-street" }}
						>
							Ulice
						</LabelledInput>
						<LabelledInput
							id="shippingAddressStreetNumber"
							required
							inputProps={{ class: "smartform-instance-address smartform-address-number" }}
						>
							čp.
						</LabelledInput>
						<LabelledInput
							id="shippingAddressCity"
							required
							inputProps={{ class: "smartform-instance-address smartform-address-city" }}
						>
							Město
						</LabelledInput>
						<LabelledInput
							id="shippingAddressZip"
							required
							minlength={3}
							inputProps={{ class: "smartform-instance-address smartform-address-zip" }}
						>
							PSČ
						</LabelledInput>
					</div>

					{#if submitted && !smartFormShippingAddress}
						Je nutné zadat platnou adresu.
					{/if}
				</fieldset>
			</div>

			<div class="hidden">
				<fieldset>
					<legend class="mb-2 text-base font-medium md:mb-3 lg:text-md">Fakturační adresa</legend>
					<div class="grid gap-x-3 gap-y-3 sm:grid-cols-[1fr,8rem] lg:grid-cols-[1fr,11rem] lg:gap-x-6">
						<LabelledInput
							id="billingAddressStreet"
							inputProps={{ class: "smartform-instance-company smartform-company-address-street" }}
						>
							Ulice
						</LabelledInput>
						<LabelledInput
							id="billingAddressStreetNumber"
							inputProps={{ class: "smartform-instance-company smartform-company-address-number" }}
						>
							čp.
						</LabelledInput>
						<LabelledInput
							id="billingAddressCity"
							inputProps={{ class: "smartform-instance-company smartform-company-address-city" }}
						>
							Město
						</LabelledInput>
						<LabelledInput
							id="billingAddressZip"
							inputProps={{ class: "smartform-instance-company smartform-company-address-zip" }}
						>
							PSČ
						</LabelledInput>
					</div>
				</fieldset>
			</div>

			<fieldset class="flex flex-col gap-2">
				<legend class="mb-2 text-base font-medium md:mb-3 lg:text-md">Denní předpoklad celých dortů*</legend>
				{#each presumedOrderAmountOptions as { value, label }}
					<LabelledRadioInput
						id="presumedOrderAmount-{value}"
						name="presumedOrderAmount"
						bind:group={selectedPresumedOrderAmount}
						{value}
						required
						note
						noteName="presumedOrderAmountSpecified"
						notePlaceholder="Přidat poznámku"
					>
						{label}
					</LabelledRadioInput>
				{/each}
			</fieldset>

			<fieldset class="flex flex-col gap-2">
				<legend class="mb-2 text-base font-medium md:mb-3 lg:text-md">Zavážení k vám do podniku*</legend>
				{#each presumedDeliveryIntervalOptions as { value, label }}
					<LabelledRadioInput
						id="presumedDeliveryInterval-{value}"
						name="presumedDeliveryInterval"
						bind:group={selectedPresumedDeliveryInterval}
						{value}
						required
						note
						noteName="presumedDeliveryIntervalSpecified"
						notePlaceholder="Přidat poznámku"
					>
						{label}
					</LabelledRadioInput>
				{/each}
			</fieldset>

			{#if $send.status === "error"}
				<Message variant="error" class="mt-4">{sendErrorMessage}</Message>
			{/if}
		</div>
		<div class="relative mt-4 w-full">
			<p class="mt-3 text-2xs">*Povinné údaje</p>
		</div>

		<div class="flex flex-col items-center gap-8">
			<GdprCheckbox />

			<Button
				class="mx-auto !rounded-full !px-8 !py-2 sm:!px-12 sm:!py-4 sm:!text-md lg:mx-0 xl:!px-28 xl:!py-[1.125rem] xl:!text-[1.375rem]"
				type="submit"
				icon={cake}
				iconClass="h-6"
				disabled={$send.status === "loading"}
			>
				{#if $send.status === "loading"}
					Odesílám…
				{:else}
					Odeslat
				{/if}
			</Button>
		</div>
	</Form>
</Smartform>
